<template>
   <div>
      <div>
         <label
            :data-cy="`code-input-${id}-label`"
            :for="id"
            class="mb-1 flex items-center capitalize leading-none"
            :class="{ 'sr-only': ariaOnlyLabel }"
            style="min-height: 2rem"
            >{{ label }}</label
         >
         <input
            :id="`code-input-${id}-input`"
            v-model="state.modelValue"
            class="block w-full rounded-lg border border-none bg-grey-10 px-3 py-4 text-black"
            type="text"
            :name="name"
            :class="{ '!border-solid !border-error-red': hasError && props.showErrors }"
            :placeholder="placeholder || $t('validation.code.placeholder', {})"
            :data-cy="`code-input-${id}-input`"
            :disabled="isDisabled"
            :minLength="codeLength"
            :maxlength="codeLength"
            @input="processInput"
         />
      </div>
      <InvalidInputsList :errorList="errorList" :class="{ hidden: !showErrors }" />
   </div>
</template>

<script setup lang="ts">
import { required, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { ValidationError } from "@/components/inputs/InvalidInputsList.vue";

const $emit = defineEmits(["input-update"]);

const { t: $t } = useI18n();

const props = withDefaults(
   defineProps<{
      id: string;
      label: string;
      name: string;
      code?: string;
      blockId?: string;
      codeLength?: number | null;
      placeholder?: string;
      showErrors?: boolean;
      isRequired?: boolean;
      isDisabled?: boolean;
      ariaOnlyLabel?: boolean;
      validationError?: ValidationError;
   }>(),
   {
      codeLength: null,
      showErrors: false,
      isRequired: false,
      isDisabled: false,
      ariaOnlyLabel: false,
      validationError: null,
      placeholder: "",
   }
);

const state = reactive({ modelValue: props.code });

const $v: any = useVuelidate(
   {
      modelValue: {
         ...(props.isRequired && { required: helpers.withMessage($t("validation.code.required"), required) }),
         ...(props.codeLength && {
            invalidLength: helpers.withMessage(
               $t("validation.code.invalid_length", { length: props.codeLength }),
               (v: string): boolean => v.length === props.codeLength
            ),
         }),
         invalidCharacters: helpers.withMessage($t("validation.code.invalid_characters"), (v: string): boolean =>
            /^[a-zA-Z0-9]+$/.test(v)
         ),
      },
   },
   state
);

const hasError = computed(() => $v.value.$error || $v.value.$invalid);

const errorList = computed(() => {
   if (hasError.value) {
      return [
         {
            inputId: props.blockId || props.id,
            validator: $v.value.$errors[0]?.$validator,
            message: $v.value.$errors[0]?.$message,
         },
      ];
   } else if (props.validationError) {
      return [props.validationError];
   }
   return [];
});

const processInput = () => {
   if (!$v.value.$dirty) $v.value.$touch();

   $emit("input-update", { value: state.modelValue, isValid: !$v.value.$invalid && !$v.value.$error });
};

processInput();
</script>
